import { useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { FiAlertTriangle, FiLoader, FiX } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "../../context/TranslationContext";
import { deleteMyAccount, getAccountDeletionCheck } from "../../api/profile";
import Button from "../ds/Button";
import Icon from "../ds/Icon";
import useLockBackgroundScroll from "../../hooks/useLockBackgroundScroll";

const money = (amount) => `₾${(Number(amount) || 0).toFixed(2)}`;

/** Refusals the server names by code, in the reader's language. */
const ERROR_KEYS = {
  wrong_password: "accountDeletion.wrongPassword",
  confirmation_mismatch: "accountDeletion.wrongUsername",
  keepz_unavailable: "accountDeletion.keepzUnavailable",
  charge_in_progress: "accountDeletion.chargeInProgress",
};

/**
 * The danger zone on your own profile: deleting your account. Opening it asks the
 * server what still stands in the way — Mint Spaces you own, money in your balance,
 * a withdrawal or campaign in motion — and only offers the delete once nothing does.
 */
const DeleteAccountSection = ({ isDark, setToast }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [check, setCheck] = useState(null); // { canDelete, blockers, subscriptions, confirmWith }
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [secret, setSecret] = useState("");
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  useLockBackgroundScroll(open);

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const inputClass = "border-transparent bg-fill-tertiary text-label-primary placeholder:text-label-tertiary focus:ring-2 focus:ring-focus-ring";

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await getAccountDeletionCheck();
      setCheck(res?.data || null);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setOpen(true);
    setSecret("");
    setError("");
    setCheck(null);
    load();
  };

  const close = () => {
    if (!deleting) setOpen(false);
  };

  const confirm = async () => {
    if (!check?.canDelete || !secret.trim() || deleting) return;
    setDeleting(true);
    setError("");
    try {
      await deleteMyAccount(check.confirmWith === "password" ? { password: secret } : { username: secret });
      logout();
      setToast?.(t("accountDeletion.deleted"));
      navigate("/", { replace: true });
    } catch (err) {
      const body = err?.response?.data || {};
      if (body.code === "blocked" && Array.isArray(body.blockers)) {
        // Something changed since the check — show what's in the way now.
        setCheck((prev) => ({ ...(prev || {}), canDelete: false, blockers: body.blockers }));
      } else {
        setError(t(ERROR_KEYS[body.code] || "accountDeletion.failed"));
      }
      setDeleting(false);
    }
  };

  const blockerText = (b) => {
    if (b.code === "balance") {
      return b.onHold > 0
        ? t("accountDeletion.blocker_balanceOnHold", { amount: money(b.amount), onHold: money(b.onHold) })
        : t("accountDeletion.blocker_balance", { amount: money(b.amount) });
    }
    return t(`accountDeletion.blocker_${b.code}`);
  };

  return (
    <div className="flex flex-col gap-space-12">
      <p className="text-caps-label uppercase text-status-danger">{t("accountDeletion.dangerZone")}</p>
      <div className="flex flex-wrap items-center justify-between gap-space-12 rounded-radius-md bg-status-danger-bg p-space-16">
        <div className="flex min-w-[14rem] flex-1 flex-col gap-space-4">
          <p className={`text-subheadline-semibold ${baseText}`}>{t("accountDeletion.title")}</p>
          <p className={`text-footnote ${mutedText}`}>{t("accountDeletion.description")}</p>
        </div>
        <Button
          variant="gray"
          size="s"
          className="!text-status-danger"
          icon={<Icon name="trash" size={16} />}
          onClick={openModal}
        >
          {t("accountDeletion.button")}
        </Button>
      </div>

      {/* On <body>: the profile card's backdrop blur would otherwise pin "fixed" to the card. */}
      {open && createPortal(
        <div
          className="fixed inset-0 z-[300] flex items-end justify-center min-[600px]:items-center min-[600px]:p-4"
          role="dialog"
          aria-modal="true"
          data-scroll-lock-modal
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />
          <div
            className={`relative max-h-[90vh] w-full max-w-[480px] overflow-y-auto rounded-t-radius-xl border p-space-24 shadow-overlay min-[600px]:rounded-radius-lg ${
              "border-separator-default bg-bg-raised"
            }`}
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-status-danger-bg">
                  <FiAlertTriangle className="h-5 w-5 text-status-danger" />
                </span>
                <h3 className={`text-title-3 ${baseText}`}>{t("accountDeletion.modalTitle")}</h3>
              </div>
              <button
                type="button"
                onClick={close}
                disabled={deleting}
                aria-label={t("common.cancel")}
                className={`shrink-0 rounded-radius-xs p-1.5 transition hover:bg-fill-quaternary`}
              >
                <FiX className={`h-5 w-5 ${mutedText}`} />
              </button>
            </div>

            {loading ? (
              <p className={`flex items-center gap-2 text-subheadline ${mutedText}`}>
                <FiLoader className="h-4 w-4 animate-spin" />
                {t("accountDeletion.checking")}
              </p>
            ) : loadError || !check ? (
              <div className="space-y-3">
                <p className="text-subheadline text-status-danger">{t("accountDeletion.checkFailed")}</p>
                <button
                  type="button"
                  onClick={load}
                  className={`rounded-radius-sm border px-4 py-2 text-subheadline-semibold ${
                    isDark ? "border-white/10 text-slate-200 hover:bg-white/5" : "border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {t("payment.retry")}
                </button>
              </div>
            ) : !check.canDelete ? (
              <div className="space-y-3">
                <p className={`text-subheadline-semibold ${baseText}`}>{t("accountDeletion.blockedIntro")}</p>
                <ul className="space-y-2">
                  {check.blockers.map((b) => (
                    <li
                      key={b.code}
                      className={`rounded-radius-sm border p-3 text-subheadline ${
                        isDark ? "border-white/10 bg-white/[0.03] text-slate-200" : "border-slate-200 bg-slate-50 text-slate-700"
                      }`}
                    >
                      <span className="flex items-start gap-2">
                        <FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-warning" />
                        <span>{blockerText(b)}</span>
                      </span>
                      {b.code === "owns_mint_spaces" && (
                        <ul className="mt-2 space-y-1 pl-6">
                          {(b.mintSpaces || []).map((space) => (
                            <li key={space._id}>
                              <Link
                                to={`/mint-space/${space._id}?nav=settings&section=danger`}
                                onClick={() => setOpen(false)}
                                className="font-semibold text-accent-text hover:underline"
                              >
                                {space.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                      {b.code === "balance" && (
                        <Link
                          to="/balance"
                          onClick={() => setOpen(false)}
                          className="mt-2 inline-block pl-6 font-semibold text-accent-text hover:underline"
                        >
                          {t("accountDeletion.goToBalance")}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className={`mb-2 text-subheadline-semibold ${baseText}`}>{t("accountDeletion.consequencesIntro")}</p>
                  <ul className={`list-disc space-y-1.5 pl-5 text-subheadline ${mutedText}`}>
                    <li>{t("accountDeletion.consequenceProfile")}</li>
                    {check.subscriptions > 0 && <li>{t("accountDeletion.consequenceSubscriptions")}</li>}
                    <li>{t("accountDeletion.consequenceSpaces")}</li>
                    <li>{t("accountDeletion.consequenceContent")}</li>
                    <li>{t("accountDeletion.consequenceEarnings")}</li>
                    <li>{t("accountDeletion.consequenceRecords")}</li>
                  </ul>
                </div>
                <label className="block">
                  <span className={`mb-1.5 block text-footnote-semibold ${mutedText}`}>
                    {check.confirmWith === "password"
                      ? t("accountDeletion.confirmPassword")
                      : t("accountDeletion.confirmUsername", { username: user?.username || "" })}
                  </span>
                  <input
                    type={check.confirmWith === "password" ? "password" : "text"}
                    autoComplete={check.confirmWith === "password" ? "current-password" : "off"}
                    autoFocus
                    value={secret}
                    onChange={(e) => {
                      setSecret(e.target.value);
                      setError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") confirm();
                    }}
                    placeholder={check.confirmWith === "password" ? t("accountDeletion.passwordPlaceholder") : user?.username || ""}
                    className={`w-full rounded-radius-sm border px-3 py-2.5 text-subheadline outline-none transition ${inputClass}`}
                  />
                </label>
                {error && <p className="text-subheadline text-status-danger">{error}</p>}
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={close}
                disabled={deleting}
                className={`flex-1 rounded-radius-sm border-2 px-4 py-2.5 text-subheadline-semibold transition disabled:opacity-50 ${
                  isDark
                    ? "border-white/15 bg-white/5 text-slate-200 hover:bg-white/10"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {t("common.cancel")}
              </button>
              {check?.canDelete && (
                <button
                  type="button"
                  onClick={confirm}
                  disabled={!secret.trim() || deleting}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-radius-sm bg-red-500 px-4 py-2.5 text-subheadline-semibold text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {deleting && <FiLoader className="h-4 w-4 animate-spin" />}
                  {deleting ? t("accountDeletion.deleting") : t("accountDeletion.confirm")}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DeleteAccountSection;
